import { useEffect, useState, useMemo, useCallback } from "react";
import BarChart from "./BarChart";
import { CameraItem } from "../../models/Camera";
import {
  IdentifiedObjectItem,
  IdentifiedSpeciesCount,
} from "../../models/Media";
import {
  CHART_BG_COLORS,
  CHART_BORDER_COLORS,
  check_date_within_range,
} from "../../utils/reports";

interface AllSpeciesProps {
  cameras: CameraItem[];
  selectedCameras: CameraItem[];
  allIdentifiedObjects: IdentifiedObjectItem[];
  selectedIdentifiedObjects: IdentifiedObjectItem[];
  reportData: IdentifiedSpeciesCount[];
  startDate: string;
  endDate: string;
}
export const getCount = (
  reportMap: Map<string, IdentifiedSpeciesCount[]>,
  startDate: string,
  endDate: string,
  object_name: string,
  camera: string
): number => {
  const key = `${object_name}-${camera}`;
  const dataList = reportMap.get(key);

  if (!dataList) return 0;

  return dataList.reduce((count: number, data: IdentifiedSpeciesCount) => {
    return check_date_within_range(startDate, endDate, data.date)
      ? count + 1
      : count;
  }, 0);
};
export const useProcessedReportData = (
  reportData: IdentifiedSpeciesCount[],
  startDate: string,
  endDate: string
) => {
  return useMemo(() => {
    const map = new Map();
    reportData.forEach((data) => {
      if (!check_date_within_range(startDate, endDate, data.date)) return;
      const key = `${data.identified_object_name}-${data.camera}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push({ date: data.date, video_count: 1 });
    });
    return map;
  }, [reportData, startDate, endDate]);
};

export const AllSpecies = ({
  cameras,
  selectedCameras,
  allIdentifiedObjects,
  selectedIdentifiedObjects,
  reportData,
  startDate,
  endDate,
}: AllSpeciesProps) => {
  const [csvData, setCsvData] = useState<any>();
  const [mapData, setMapData] = useState<any>(null);
  const dataMap = useProcessedReportData(reportData, startDate, endDate);

  const ChartName = "All Species";

  const csvArrays = useCallback(
    (objectName: string) =>
      cameras.map((camera) => ({
        camera: camera.camera_id,
        Object: objectName,
        Count: getCount(
          dataMap,
          startDate,
          endDate,
          objectName,
          camera.camera_id
        ),
      })),
    [cameras, dataMap, startDate, endDate]
  );

  useEffect(() => {
    setCsvData(selectedIdentifiedObjects.flatMap((obj) => csvArrays(obj.name)));
  }, [
    selectedCameras,
    selectedIdentifiedObjects,
    startDate,
    endDate,
    csvArrays,
  ]);

  useEffect(() => {
    if (selectedCameras && selectedIdentifiedObjects) {
      const allSpecies = selectedIdentifiedObjects.map(
        (species) => species.name
      );
      const totalCounts = allSpecies.map((speciesName) =>
        csvArrays(speciesName).reduce((sum, item) => sum + item.Count, 0)
      );

      setMapData({
        labels: allSpecies,
        datasets: [
          {
            data: totalCounts,
            backgroundColor: Object.values(CHART_BG_COLORS),
            borderColor: Object.values(CHART_BORDER_COLORS),
            borderWidth: 1,
          },
        ],
      });
    }
  }, [selectedCameras, selectedIdentifiedObjects, csvArrays]);

  const getYAxisConfig = (datasets: any[]) => {
    if (!datasets || datasets.length === 0)
      return { suggestedMax: 10, stepSize: 1 };

    const maxValue = Math.max(
      ...datasets.flatMap((dataset) => dataset.data || [0])
    );

    let stepSize = 1;
    let suggestedMax = 10;

    if (maxValue < 10) {
      stepSize = 1;
      suggestedMax = 10;
    } else if (maxValue < 70) {
      stepSize = 10;
      suggestedMax = Math.ceil(maxValue / 10) * 10;
    } else if (maxValue < 350) {
      stepSize = 50;
      suggestedMax = Math.ceil(maxValue / 50) * 50;
    } else if (maxValue < 1000) {
      stepSize = 50;
      suggestedMax = Math.ceil(maxValue / 50) * 50;
    } else {
      stepSize = 200;
      suggestedMax = Math.ceil(maxValue / 200) * 200;
    }

    return { suggestedMax, stepSize };
  };

  const { suggestedMax, stepSize } = mapData
    ? getYAxisConfig(mapData.datasets)
    : { suggestedMax: 10, stepSize: 1 };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false, position: "top" as const },
      title: {
        display: true,
        text: "Total Observed Species Count",
        position: "top" as const,
      },
    },
    layout: { padding: { left: 0, right: 10, top: 10, bottom: 0 } },
    scales: {
      y: { min: 0, suggestedMax, ticks: { stepSize } },
    },
  };

  return (
    <BarChart
      mapData={mapData}
      options={options}
      csvData={csvData}
      name={ChartName}
    />
  );
};
